import { createMemo, type ParentComponent, Show, untrack } from "solid-js";
import CustomerId from "../state/CustomerId";
import CustomerService from "../services/CustomerService";
import { CustomerProvider } from "../state/customer";
import type { RouteSectionProps } from "@solidjs/router";
import { Navigate } from "@solidjs/router";
import ApiService from "../services/ApiService";
import posthog from "posthog-js";
import uuid from "../utils/uuid";
import { POSTHOG_PROPERTIES } from "../services/PostHogService";
import FeatureService from "../services/FeatureService";
import CustomerResourceService from "../services/CustomerResourceService";
import ServiceAccountService from "../services/ServiceAccountService";
import ResourceNotFoundPage from "./ResourceNotFoundPage";
import { NON_RETRYABLE_STATUS_CODES } from "../utils/apiUtil";
import CustomerRegistrationFileService from "../services/CustomerRegistrationFileService";
import { useLocalUserSettingsDispatch } from "../state/localUserSettings";
import LegacyPlatformService from "../services/LegacyPlatformService";
import TrustService from "../services/TrustService";
import { keyFromSelector } from "i18next";
import { BannersProvider } from "./customer/banner/banners";
import { banners } from "./customer/banner/bannerConfigs";
import { isNil } from "lodash-es";
import { TextButton } from "../components/button/Button.tsx";
import PermissionService from "../services/PermissionService.ts";
import { useUserState } from "../state/user.tsx";
import UserService from "../services/UserService.ts";
import BillingService from "../services/BillingService.ts";

const CustomerLoader: ParentComponent<RouteSectionProps> = (props) => {
  const customerId = untrack(() => CustomerId.from(props.params.customerId!));
  const user = useUserState();
  const users = UserService.getPaginatedUsers(customerId, () => ({ page: 0, size: 1 }));

  const customerQuery = CustomerService.getCustomer(customerId);

  // Warm the trust cache up front so trust-gated UI (e.g. the form share button) doesn't flicker once it loads.
  TrustService.getTrustLevel(customerId);

  const constructImpersonationUrl = (customerId: CustomerId) => {
    const searchParams = new URLSearchParams(globalThis.location.search);
    searchParams.append("impersonate-customer", customerId.toString());
    return globalThis.location.pathname + "?" + searchParams.toString();
  };

  const isOnlyUserForCustomer = createMemo(() => users.data?.last ?? false);

  return (
    <>
      {/* Redirects the user to the onboarding flow when all the following are true:
          - The finishedTour database flag on the user is false
              The current user has never completed the onboarding
          - The current user is the only user for this customer
              Adding new users can only be done after the onboarding is started, so onboarding should never be started
              when there are already multiple users
          - The current user is the account owner for this customer
              When a new customer is created, the corresponding new user is always the account owner for this customer.
              Users that are not an account owner can never end up in the onboarding
       */}
      <Show
        when={
          !user().finishedTour &&
          isOnlyUserForCustomer() &&
          PermissionService.isAccountOwner(customerId) &&
          location.pathname !== `/customers/${customerId}/onboarding`
        }
      >
        <Navigate href={`/customers/${customerId}/onboarding`} />
      </Show>
      <Show when={!isNil(customerQuery.error) && NON_RETRYABLE_STATUS_CODES.includes(customerQuery.error.status)}>
        <>
          <ResourceNotFoundPage
            resource={keyFromSelector(($) => $.RESOURCE_CUSTOMER)}
            illustration={{ primaryIcon: "lockClosed" }}
          />

          <Show when={PermissionService.isAdmin()}>
            <TextButton
              variant="primary"
              color="destructive"
              text="Impersonate"
              class="mx-auto"
              onClick={() => {
                globalThis.location.href = constructImpersonationUrl(customerId);
              }}
            />
          </Show>
        </>
      </Show>
      <Show when={customerQuery.data}>
        {(customer) => (
          <CustomerProvider value={customer()}>
            <ServiceInitializer customerId={customerId}>
              <BannersProvider value={{ banners }}>{props.children}</BannersProvider>
            </ServiceInitializer>
          </CustomerProvider>
        )}
      </Show>
    </>
  );
};

const ServiceInitializer: ParentComponent<{ customerId: CustomerId }> = (props) => {
  ApiService.init(props.customerId);
  BillingService.init(props.customerId);
  FeatureService.init();
  CustomerResourceService.init(props.customerId);
  CustomerRegistrationFileService.init(props.customerId);
  ServiceAccountService.init(props.customerId);
  LegacyPlatformService.init(props.customerId);

  useLocalUserSettingsDispatch()("lastCustomerId", props.customerId);

  posthog.register({
    [POSTHOG_PROPERTIES.CUSTOMER_ID]: props.customerId,
    visit_id: uuid(),
  });
  posthog.setPersonProperties({ [POSTHOG_PROPERTIES.CUSTOMER_ID]: props.customerId });

  return props.children;
};

export default CustomerLoader;
